import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { Locale } from "@nh/shared";
import { Alert } from "@/shared/ui/core";
import { FormGrid, FormSection } from "@/features/admin/components";
import { ProductLocalePanel } from "../ProductLocalePanel";
import { editorialFillState, type ProductFormValues } from "../../productForm";

export interface EditorialPanelProps {
  /** El título de la tarjeta del paso. */
  title: string;
  description: string;
  /** Los bloques del paso, ya en el idioma que toca. */
  children: (locale: Locale) => ReactNode;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * La tarjeta de un paso de la ficha editorial: pestañas de idioma y la
 * rejilla de bloques.
 *
 * Es lo que era `EditorialStep` menos la lista de los once bloques: los
 * bloques ahora los pone cada paso (`detail-steps/`) y esto es el marco
 * que comparten. Sin él, los cuatro pasos copiarían cuatro veces el
 * panel de idioma, el aviso del inglés y la rejilla — y el día que el
 * aviso cambie, cambiaría en tres.
 *
 * ── Ningún bloque bloquea el guardado ───────────────────────────────
 * La ficha del sistema actual no es una tabla de datos: es una pieza
 * escrita, y escribirla lleva tiempo. Por eso TODO bloque es opcional en
 * el contrato y por eso esta pantalla, vacía entera, no impide guardar.
 * Un producto sin editorial es válido y su ficha enseña lo que tenga:
 * media ficha es mejor que una plantilla con huecos que gritan "falta
 * contenido", y muchísimo mejor que un producto sin publicar esperando a
 * que alguien tenga una tarde libre.
 *
 * ── El estado de cada idioma, a la vista ────────────────────────────
 * `editorialFillState` mira el editorial ENTERO —los once bloques, no
 * los de este paso— porque eso es lo que dice la pestaña: si el inglés
 * tiene algo escrito o está en blanco. Un estado por paso convertiría la
 * pestaña de idioma en un segundo indicador de progreso.
 *
 * ── Los bloques, en tres columnas ───────────────────────────────────
 * Plegados, cada bloque es un renglón de título y descripción. En tres
 * columnas el índice del paso cabe de un vistazo, que es justo para lo
 * que sirve estar plegado. El bloque ABIERTO se lleva la fila entera —lo
 * hace él mismo, ver `EditorialBlock`—: sus campos son títulos,
 * entradillas y listas que crecen, y en un tercio de fila no se escriben.
 * ═══════════════════════════════════════════════════════════════════
 */
export function EditorialPanel({ title, description, children }: EditorialPanelProps) {
  const { t } = useTranslation();
  const { watch } = useFormContext<ProductFormValues>();
  const editorial = watch("editorial");
  const currencies = watch("currencies");

  return (
    <FormSection
      title={t(title)}
      description={t(description)}
      columns={1}
      compact
      aside={null}
    >
      <ProductLocalePanel
        states={{
          es: editorialFillState(editorial.es, currencies),
          en: editorialFillState(editorial.en, currencies),
        }}
      >
        {(locale) => (
          <>
            {locale === "en" && (
              <Alert variant="info">
                {t(
                  "La ficha en inglés cae al español BLOQUE a bloque: lo que no escribas aquí se lee en español, entero.",
                )}
              </Alert>
            )}

            <FormGrid columns={3} className="gap-3">
              {children(locale)}
            </FormGrid>
          </>
        )}
      </ProductLocalePanel>
    </FormSection>
  );
}
